import React, { useMemo, useState } from "react";
import { cilCode, cilCopy, cilInfo } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { CSpinner, CTooltip } from "@coreui/react";
import { ColoredValue } from "@src/components/ColoredValue";
import { useJsonModal } from "@src/hooks/useJsonModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import type { TDT } from "@src/types";
import type { WinLossValueType } from "@src/types";
import { EXIT_ACTION_IDS } from "@src/utils/constants";
import {
  type PositionRow,
  buildPositionExport,
  formatPositionAsJSON,
  parseNumericValue,
} from "@src/utils/formatters";
import { onKeyboardActivate } from "@src/utils/keyboardActivate";
import {
  getTrackingEndedMovementTime,
  isPositionTrackingEnded,
  isTrackingEndedFactChartEligible,
  resolvePositionExecutionInterval,
} from "@src/utils/positionStatus";

import { useRunResultContext } from "./RunResultContext";

type TableRow = {
  id: number;
  position: string;
  type: string;
  dateTime: string;
  price: string;
  leverage: string;
  positionSize: string;
  contracts: string;
  fees: string;
  duration?: string;
  profit?: string;
  profitPercent?: string;
};

interface Props {
  movement: TDT.ApiPositionMovement;
  position: T.AppPosition;
  rowData: TableRow;
  column: "left" | "right";
  positionRow: PositionRow;
  isOpen: boolean;
}

export const RunMovementCell: React.FC<Props> = ({
  movement,
  position,
  rowData,
  column,
  positionRow,
  isOpen,
}) => {
  const runType = position.execution_id ? "execution" : "backtest";
  const positionId = movement.position_id;

  const { showJsonModal } = useJsonModal();
  const { showToast } = useToastActions();

  const { tradingViewWidgetRef, tradingViewContainerRef, datafeedRef, run } = useRunResultContext();
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [isLoadingPosition, setIsLoadingPosition] = useState(false);

  const isMovementIdColumn = column === "right";

  // Handle movement click navigation
  const handleMovementClick = () => {
    if (!tradingViewWidgetRef.current) return;
    const trackingEnded = isPositionTrackingEnded(position);
    const cachedBounds = trackingEnded ? datafeedRef.current?.state?.trackingEndedFactBounds : null;
    const factTime = trackingEnded ? getTrackingEndedMovementTime(movement) : null;
    const interval = trackingEnded
      ? resolvePositionExecutionInterval(position, {
          start_ts: run?.start_ts ?? datafeedRef.current?.props?.start_ts,
          end_ts: run ? run.end_ts : datafeedRef.current?.props?.end_ts,
          executionBoundaries: datafeedRef.current?.props?.executionBoundaries,
          group: !!run && "_is_group_synthetic" in run && !!run._is_group_synthetic,
        })
      : null;
    if (trackingEnded && !isTrackingEndedFactChartEligible(factTime!, cachedBounds, interval))
      return;

    // Scroll to TradingView widget first
    if (!trackingEnded && tradingViewContainerRef.current) {
      tradingViewContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    // Tracking-ended facts use their recorded-time helper; legacy rows fall back to create_ts.
    const timestampField = trackingEnded ? factTime : movement.movement_ts || movement.create_ts;
    if (timestampField) {
      try {
        const timestamp = new Date(timestampField.replace("Z", "+00:00")).getTime() / 1000;
        const chart = tradingViewWidgetRef.current.activeChart();
        const centerTime = Math.floor(timestamp);

        // Calculate view range based on position duration or minimum view window
        // Similar logic to getInitialRangeFromPositions
        const positionStart = position.start_ts
          ? new Date(position.start_ts).getTime() / 1000
          : centerTime;
        const positionEnd = trackingEnded
          ? timestamp
          : position.end_ts
            ? new Date(position.end_ts).getTime() / 1000
            : centerTime;
        const positionDuration = Math.abs(positionEnd - positionStart);

        // Minimum view windows based on typical chart resolutions
        // These provide comfortable viewing for short positions
        const MIN_VIEW_SECONDS = 60 * 60 * 4; // 4 hours minimum (good for 1m-15m charts)
        const MAX_VIEW_SECONDS = 60 * 60 * 24 * 7; // 7 days maximum

        // Use position duration with 30% padding, but ensure minimum view window
        const viewDuration = Math.max(
          Math.min(positionDuration * 1.3, MAX_VIEW_SECONDS),
          MIN_VIEW_SECONDS,
        );

        // Determine how to position the view:
        // - If position fits completely: center on position midpoint
        // - If position doesn't fit: put open at 1/4 of viewport (more space to the right for scrolling)
        const positionFitsInView = positionDuration <= viewDuration * 0.8; // 80% to leave some padding
        const positionMidpoint = (positionStart + positionEnd) / 2;

        let targetRange: { from: number; to: number };
        if (positionFitsInView) {
          // Center on position midpoint
          targetRange = {
            from: positionMidpoint - viewDuration / 2,
            to: positionMidpoint + viewDuration / 2,
          };
        } else {
          // Position open at 1/4 of viewport (humans read left-to-right, drag right to see more)
          const quarterView = viewDuration / 4;
          targetRange = {
            from: positionStart - quarterView,
            to: positionStart + viewDuration - quarterView,
          };
        }

        if (trackingEnded && cachedBounds) {
          targetRange = {
            from: Math.min(cachedBounds.to, Math.max(cachedBounds.from, targetRange.from)),
            to: Math.max(cachedBounds.from, Math.min(cachedBounds.to, targetRange.to)),
          };
          if (
            targetRange.to <= targetRange.from ||
            timestamp < targetRange.from ||
            timestamp > targetRange.to
          ) {
            targetRange = {
              from: Math.max(cachedBounds.from, timestamp - MIN_VIEW_SECONDS / 2),
              to: Math.min(cachedBounds.to, timestamp + MIN_VIEW_SECONDS / 2),
            };
          }
          if (targetRange.to <= targetRange.from) return;
        }
        if (trackingEnded && tradingViewContainerRef.current) {
          tradingViewContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        setTimeout(async () => {
          try {
            await chart.setVisibleRange(targetRange);
            if (trackingEnded) return;

            // Verify and retry if range doesn't match (TradingView sometimes ignores range requests)
            const actualRange = chart.getVisibleRange();
            const rangeMatches =
              Math.abs(actualRange.from - targetRange.from) < 3600 &&
              Math.abs(actualRange.to - targetRange.to) < 3600;

            if (!rangeMatches) {
              chart.resetData();
              await new Promise((resolve) => setTimeout(resolve, 300));
              await chart.setVisibleRange(targetRange);
            }
          } catch {
            // Navigation failed silently - user can manually scroll
          }
        }, 300);
      } catch (error) {
        console.error("Failed to navigate to movement timestamp:", error);
      }
    }
  };

  // Handle position copy
  const handleCopyPosition = () => {
    if (!positionRow.open && !positionRow.close) return;

    const positionData = buildPositionExport(positionRow);
    const text = formatPositionAsJSON(positionData);
    navigator.clipboard.writeText(text).catch((err) => {
      console.error("Failed to copy:", err);
    });
  };

  // Determine what to render based on column and movement type
  const getHeaderConfig = () => {
    if (isMovementIdColumn) {
      return {
        title: "MovementId",
        directionLabel: undefined,
        onClick: undefined,
        copyHandler: undefined,
      };
    }
    if (isOpen) {
      // Direction label: Long/Short for futures, Buy for spot (rendered separately)
      const directionLabel =
        position.type_name === "spot"
          ? "Buy"
          : position.direction_name === "long"
            ? "Long"
            : "Short";
      return {
        title: `Position #${positionId}`,
        directionLabel,
        onClick: handleMovementClick,
        copyHandler: handleCopyPosition,
      };
    }
    // Partial close = exit amount differs from entry amount (position not fully closed)
    // A missing end_ts is not a financial close: the position can be open or tracking-ended.
    // Note: Only considers the first exit movement found - multiple partial closes not yet supported
    // Exit is always SELL (SELL_SPOT/SELL_FUTURE) regardless of direction
    const exitMovement = position.movements?.find((m) => EXIT_ACTION_IDS.includes(m.action_id));
    const isPartialClose =
      exitMovement?.amount != null &&
      position.entry_amount != null &&
      Math.abs(exitMovement.amount - position.entry_amount) > 0.0001; // Tolerance for float comparison
    const closeTitle = isPartialClose ? "Close - PARTIAL ⚠" : "Close";
    return {
      title: closeTitle,
      directionLabel: undefined,
      onClick: undefined,
      copyHandler: undefined,
    };
  };

  interface FieldConfig {
    label: string;
    key: keyof TableRow;
    coloredValue?: {
      type: WinLossValueType;
      threshold?: number;
      decimals?: number;
      suffix?: string;
    };
  }

  const fields = useMemo((): FieldConfig[] => {
    if (isOpen && column === "left") {
      return [
        { label: "Date:", key: "dateTime" },
        { label: "Price:", key: "price", coloredValue: { type: "usdt", decimals: 4 } },
        { label: "Leverage:", key: "leverage" },
      ];
    }
    if (isOpen && column === "right") {
      return [
        { label: "Size:", key: "positionSize", coloredValue: { type: "usdt", decimals: 2 } },
        { label: "Contracts:", key: "contracts", coloredValue: { type: "number", decimals: 4 } },
        { label: "Fees:", key: "fees", coloredValue: { type: "usdt", decimals: 4 } },
      ];
    }
    if (!isOpen && column === "left") {
      return [
        { label: "Date:", key: "dateTime" },
        { label: "Price:", key: "price", coloredValue: { type: "usdt", decimals: 4 } },
        { label: "Duration:", key: "duration" },
      ];
    }
    // Close right
    return [
      { label: "Fees:", key: "fees", coloredValue: { type: "usdt", decimals: 4 } },
      {
        label: "Profit:",
        key: "profit",
        coloredValue: { type: "usdt", decimals: 4, threshold: 0 },
      },
      {
        label: "Profit %:",
        key: "profitPercent",
        coloredValue: { type: "percentage", decimals: 2, threshold: 0 },
      },
    ];
  }, [isOpen, column]);

  const headerConfig = getHeaderConfig();

  const handleShowRawMovement = async (e: React.MouseEvent) => {
    e.stopPropagation();

    setIsLoadingOrder(true);

    try {
      // Build the data structure: movement fields at root, with order as nested sub-key
      const movementData: Record<string, unknown> = {
        ...movement,
      };

      // Check if order is already embedded
      if (movement.order) {
        movementData.order = movement.order;
      }

      // If execution and has order_id, fetch with embedded order if not already present
      if (runType === "execution" && movement.order_id && !movement.order) {
        const movementWithOrder = await tdApi.getPositionMovement(positionId, movement.id, {
          embed: "order",
        });

        if (movementWithOrder?.order) {
          movementData.order = movementWithOrder.order;
        }
      }

      showJsonModal(movementData, `Movement Data - Movement #${movement.id}`);
    } catch (error) {
      console.error("Failed to fetch movement data:", error);
      showToast({
        type: "danger",
        message: `Failed to fetch movement data: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    } finally {
      setIsLoadingOrder(false);
    }
  };

  const handleCopyMovement = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(movement, null, 2)).catch((err) => {
      console.error("Failed to copy:", err);
    });
  };

  const handleShowRawPosition = async (e: React.MouseEvent) => {
    e.stopPropagation();

    setIsLoadingPosition(true);

    try {
      // Fetch the full position data from API
      const apiPosition = await tdApi.getPosition(positionId);

      if (!apiPosition) {
        showToast({
          type: "warning",
          message: "No position data available",
        });
        return;
      }

      // Fetch movements for this position (snapshot of DB)
      const movementsResponse = await tdApi.getPositionMovements(positionId, {
        limit: 100, // Get all movements for the position
      });

      // Build position data with movements
      const positionData: Record<string, unknown> = {
        ...apiPosition,
        movements: movementsResponse?.items || [],
      };

      showJsonModal(positionData, `Raw Position Data - Position #${positionId}`);
    } catch (error) {
      console.error("Failed to fetch raw position:", error);
      showToast({
        type: "danger",
        message: `Failed to fetch raw position: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    } finally {
      setIsLoadingPosition(false);
    }
  };

  const getReasonTooltip = (): string | null => {
    // Show tooltip if we have any reason info (backtest or execution)
    if (!movement.reason_key && !movement.reason_note && !movement.execution_command_id) {
      return null;
    }
    const parts: string[] = [];
    if (movement.reason_key) parts.push(movement.reason_key);
    if (movement.reason_note) parts.push(`"${movement.reason_note}"`);
    if (movement.execution_command_id) parts.push(`cmd #${movement.execution_command_id}`);
    return parts.join(" | ");
  };

  const reasonTooltip = getReasonTooltip();

  const renderKeyValueCell = (field: FieldConfig) => {
    const value = rowData?.[field.key] || "";

    const renderValue = () => {
      if (field.coloredValue && value) {
        const numValue = parseNumericValue(String(value));
        return (
          <ColoredValue
            value={numValue}
            type={field.coloredValue.type}
            decimals={field.coloredValue.decimals}
            threshold={field.coloredValue.threshold}
            size="s"
          />
        );
      }
      return value;
    };

    return (
      <div key={field.key} className="d-flex justify-content-between data-value-row">
        <strong>{field.label}</strong>
        <span className="ms-auto">{renderValue()}</span>
      </div>
    );
  };

  return (
    <div className="RunMovementCell flex-column">
      <div className="d-flex justify-content-between data-value-row mb-2">
        {headerConfig.title === "MovementId" ? (
          <div className="d-flex align-items-center gap-2 w-100">
            <div className="d-flex align-items-center gap-1">
              <small
                className="field-clickable movement-id"
                role="button"
                tabIndex={0}
                onClick={handleMovementClick}
                onKeyDown={onKeyboardActivate(handleMovementClick)}
              >
                MovementId: {movement.id}
              </small>
              {isLoadingOrder ? (
                <CSpinner size="sm" />
              ) : (
                <CIcon
                  icon={cilCode}
                  size="sm"
                  className="text-muted"
                  style={{ cursor: "pointer" }}
                  onClick={handleShowRawMovement}
                  title="Show raw movement with order data"
                />
              )}
              {reasonTooltip && (
                <CTooltip content={reasonTooltip}>
                  <CIcon
                    icon={cilInfo}
                    size="sm"
                    className="text-info"
                    style={{ cursor: "help" }}
                  />
                </CTooltip>
              )}
            </div>
            <div className="ms-auto">
              <CIcon
                icon={cilCopy}
                size="sm"
                className="text-muted"
                style={{ cursor: "pointer" }}
                onClick={handleCopyMovement}
                title="Copy movement as friendly JSON"
              />
            </div>
          </div>
        ) : (
          <div className="d-flex align-items-center gap-2 w-100">
            <div className="d-flex align-items-center gap-1">
              <strong
                className={headerConfig.onClick ? "field-clickable" : ""}
                role={headerConfig.onClick ? "button" : undefined}
                tabIndex={headerConfig.onClick ? 0 : undefined}
                onClick={headerConfig.onClick}
                onKeyDown={
                  headerConfig.onClick ? onKeyboardActivate(headerConfig.onClick) : undefined
                }
              >
                {headerConfig.title}
              </strong>
              {headerConfig.directionLabel && (
                <span className="text-muted">- {headerConfig.directionLabel}</span>
              )}
              {headerConfig.copyHandler && (
                <>
                  {isLoadingPosition ? (
                    <CSpinner size="sm" />
                  ) : (
                    <CIcon
                      icon={cilCode}
                      size="sm"
                      className="text-muted"
                      style={{ cursor: "pointer" }}
                      onClick={handleShowRawPosition}
                      title="Show raw position and with movements data"
                    />
                  )}
                </>
              )}
            </div>
            {headerConfig.copyHandler && (
              <div className="ms-auto">
                <CIcon
                  icon={cilCopy}
                  size="sm"
                  className="text-muted"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    headerConfig.copyHandler!();
                  }}
                  title="Copy position as friendly JSON"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {fields.map((field) => renderKeyValueCell(field))}
    </div>
  );
};
